/**
 * JarvisAvatar
 *
 * Animierter Kiki-Avatar im Header. Spielt state-basiert vorgerenderte
 * MP4-Loops ab (Kling 2.6, generiert via kie.ai). State-Wechsel werden
 * mit Crossfade (framer-motion) überblendet, sodass keine harten Cuts
 * sichtbar sind.
 *
 * Phase-1 ist Pure UI — die ConversationalAI-Verkettung mit ElevenLabs
 * (Klick → signed-url → WebRTC → state="speaking") folgt in Phase 2.
 *
 * Background-Trick:
 *   Die Loops haben konstanten reinen weißen Hintergrund (#FFFFFF).
 *   Mit `mix-blend-mode: multiply` wird Weiß zu durchsichtig auf hellem
 *   Header-Background, dunkle Pixel (Kiki selbst) bleiben sichtbar.
 *   Das ist ein günstiger „freistellen-Effekt" ohne dass die Videos
 *   Alpha-Channel brauchen.
 */

import { useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const LOOPS = {
  idle: '/jarvis/kiki-idle.mp4',
  listening: '/jarvis/kiki-listening.mp4',
  speaking: '/jarvis/kiki-speaking.mp4',
  thinking: '/jarvis/kiki-thinking.mp4',
};

// Per-State Playback-Speed. Idle wird leicht verlangsamt, damit das Atmen
// ruhiger wirkt; speaking/listening/thinking bleiben auf normaler
// Geschwindigkeit, weil sie konversations-reaktiv sind.
const PLAYBACK_RATES = {
  idle: 0.85,
  listening: 1.0,
  speaking: 1.0,
  thinking: 1.0,
};

export default function JarvisAvatar({
  state = 'idle',
  size,
  onClick,
  className = '',
}) {
  const src = LOOPS[state] || LOOPS.idle;
  const innerRef = useRef(null);
  // Wenn ein numerisches `size` gesetzt ist, nehmen wir inline-Pixel (z.B.
  // für Floating-Buttons). Sonst überlässt die Größenkontrolle dem
  // mitgegebenen `className` (Tailwind responsive: h-12 lg:h-16 xl:h-20).
  const sizeStyle = size ? { width: size, height: size } : undefined;

  // Click-Feedback: einmaliger Inner-Pulse via re-trigger einer CSS-
  // Animation. Layout-safe (nur opacity/scale auf das innere Video-
  // Wrapper-Element), kein Outer-Ring, kein Bounding-Box-Wachstum.
  const handleClick = (e) => {
    if (innerRef.current) {
      const node = innerRef.current;
      node.classList.remove('jarvis-click-pulse');
      // Force reflow damit die Animation erneut startet
      void node.offsetWidth;
      node.classList.add('jarvis-click-pulse');
    }
    if (onClick) onClick(e);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`Kiki — ${state}`}
      className={`relative inline-block overflow-hidden focus:outline-none ${className}`}
      style={{ background: 'white', ...sizeStyle }}
    >
      <span ref={innerRef} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.video
            key={state}
            src={src}
            autoPlay
            loop
            muted
            playsInline
            onLoadedMetadata={(e) => {
              e.target.playbackRate = PLAYBACK_RATES[state] ?? 1.0;
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 w-full h-full object-contain"
          />
        </AnimatePresence>
      </span>
    </button>
  );
}
